import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, ExternalLink, ArrowLeft, ChevronRight, Instagram, Phone, Globe, ShoppingBag } from 'lucide-react';
import SEO from './SEO';
import { useManagedCollection, slugify } from '../admin/useManagedCollection';
import { SHOPPING_STORES, shoppingSlug, FEATURED_STORE_NAMES, fixStoreDescription } from './Shopping';
import SocialLinks, { countSocialLinks } from './SocialLinks';

const BASE_URL = 'https://dallasdesiscoop.com';

const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1200&h=800&fit=crop';

// The platform datatable declares the multiselect as a `string` column, so a
// saved array comes back comma-joined. Same helper as Shopping.tsx.
function toTypeArray(v: unknown): string[] {
  if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean);
  if (typeof v === 'string') return v.split(',').map((x) => String(x).trim()).filter(Boolean);
  return [];
}

const CATEGORY_LABELS: Record<string, string> = {
  Clothing: 'Clothing & Ethnic Wear',
  Jewelry: 'Jewelry',
  Accessories: 'Accessories',
  'Global Brands': 'Global Brands',
};

const CATEGORY_OFFERINGS: Record<string, string> = {
  Clothing:
    'Indian and Pakistani clothing, ethnic wear, sarees, lehengas, salwar suits, kurtas, bridal and party wear, and contemporary Desi fashion.',
  Jewelry:
    'Gold, diamond, kundan, polki, and fashion jewelry, including bridal jewelry sets and everyday pieces.',
  Accessories:
    'Clutches, potlis, dupattas, shawls, capes, and wedding accessories.',
  'Global Brands':
    'Popular international fashion and lifestyle brands.',
};

function resolveMapsUrl(s: any) {
  const address = (s.address || '').trim();
  if (address && !address.startsWith('@') && /\d/.test(address)) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${s.name}, ${address}`)}`;
  }
  return '';
}

export default function ShoppingDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const mc = useManagedCollection<any>('shopping', SHOPPING_STORES, (s, i) =>
    slugify(s.name || String(i))
  );

  const found = mc.items.find((s: any) => shoppingSlug(s) === slug);
  // Typo guard: correct "hamdpicked" to "handpicked" (House of Mykaa dashboard
  // description) so the About text, meta description, and JSON-LD all render
  // the corrected copy.
  const entry = found
    ? { ...found, description: fixStoreDescription(found.description) }
    : found;

  if (mc.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-[#082f6d] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div
        className="min-h-screen bg-white flex flex-col items-center justify-center px-4"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <SEO
          title="Store Not Found | Dallas Desi Scoop"
          description="This shopping listing could not be found."
          canonical="/shopping"
          noindex
        />
        <p className="text-gray-500 text-lg mb-4">Store not found.</p>
        <Link
          to="/shopping"
          className="text-[#082f6d] font-semibold hover:underline"
        >
          ← Back to Shopping
        </Link>
      </div>
    );
  }

  const isFeatured = FEATURED_STORE_NAMES.has(entry.name);
  const cats = toTypeArray(entry.categories);
  const singleCategory = typeof entry.category === 'string' ? entry.category : '';
  const allCats = cats.length > 0 ? cats : singleCategory ? [singleCategory] : [];
  const hasCat = (c: string) => allCats.includes(c);

  const isOnline =
    (entry.location || '').toLowerCase() === 'online' ||
    (entry.address || '').trim().startsWith('@') ||
    !(entry.address || '').trim();

  const rawLocation = (entry.location || '').trim();
  const cityLabel = isOnline
    ? 'DFW & Online'
    : rawLocation.toLowerCase() === 'other'
      ? 'DFW'
      : rawLocation.includes('TX')
        ? rawLocation
        : `${rawLocation || 'DFW'}, TX`;

  const typeLabel =
    allCats.length > 0
      ? allCats.map((c) => CATEGORY_LABELS[c] || c).join(' & ')
      : 'Desi Shopping';

  const schemaType =
    hasCat('Jewelry') && !hasCat('Clothing')
      ? 'JewelryStore'
      : hasCat('Clothing') && !hasCat('Jewelry')
        ? 'ClothingStore'
        : 'Store';

  const resolvedMapsUrl = resolveMapsUrl(entry);
  const hasAnySocial = countSocialLinks(entry) > 0;
  const canonicalPath = `/shopping/${slug}`;

  const title = `${entry.name} | ${typeLabel} in ${cityLabel} | Dallas Desi Scoop`;
  const metaDescription = `${entry.name} is ${isOnline ? 'an online Desi boutique serving the Dallas-Fort Worth community' : `a ${typeLabel.toLowerCase()} store in ${cityLabel}`}.${entry.description ? ` ${entry.description}` : ''} Find address, contact details, products, and more on Dallas Desi Scoop.`;

  const addressParts = (entry.address || '').split(',').map((p: string) => p.trim()).filter(Boolean);
  const streetAddress =
    addressParts.length >= 3 ? addressParts.slice(0, -2).join(', ') : undefined;
  const addressLocality =
    addressParts.length >= 2 ? addressParts[addressParts.length - 2] : undefined;

  const jsonLd = [
    {
      '@type': schemaType,
      name: entry.name,
      image: entry.image || FALLBACK_IMAGE,
      description: entry.description || `${entry.name}, ${typeLabel} in ${cityLabel}, serving the Dallas-Fort Worth Desi community.`,
      address:
        !isOnline && addressParts.length >= 2
          ? {
              '@type': 'PostalAddress',
              streetAddress,
              addressLocality: addressLocality || undefined,
              addressRegion: 'TX',
              addressCountry: 'US',
            }
          : undefined,
      telephone: entry.phone || undefined,
      url:
        entry.shopUrl ||
        entry.instagramUrl ||
        entry.instagram ||
        entry.website ||
        `${BASE_URL}${canonicalPath}`,
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Shopping', item: `${BASE_URL}/shopping` },
        { '@type': 'ListItem', position: 3, name: entry.name, item: `${BASE_URL}${canonicalPath}` },
      ],
    },
  ];

  // Internal linking: other stores in the same city first, then stores sharing
  // a category. Each links to its own detail page.
  const relatedStores = mc.items
    .filter((s: any) => shoppingSlug(s) !== slug)
    .map((s: any) => {
      const sCats = toTypeArray(s.categories);
      const sSingle = typeof s.category === 'string' ? s.category : '';
      const sAll = sCats.length > 0 ? sCats : sSingle ? [sSingle] : [];
      const sameCity = !isOnline && (s.location || '') === (entry.location || '');
      const sharedCat = sAll.some((c: string) => allCats.includes(c));
      return { store: s, sameCity, sharedCat };
    })
    .filter((r: any) => r.sameCity || r.sharedCat)
    .sort((a: any, b: any) => Number(b.sameCity) - Number(a.sameCity))
    .slice(0, 4);

  return (
    <div className="min-h-screen bg-[#f9fafb]" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <SEO
        title={title}
        description={metaDescription}
        canonical={canonicalPath}
        ogImage={entry.image || FALLBACK_IMAGE}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: '260px' }}>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url("${entry.image || FALLBACK_IMAGE}")` }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom, rgba(8,47,109,0.75) 0%, rgba(8,47,109,0.55) 55%, rgba(8,47,109,0.88) 100%)',
          }}
        />

        <div className="relative z-10 max-w-[1000px] mx-auto px-4 pt-6 pb-10 flex flex-col">
          <motion.div
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="self-start flex items-center gap-1.5 mb-5"
          >
            <Link
              to="/shopping"
              className="flex items-center gap-1.5 text-white/80 hover:text-white text-[12px] font-medium transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Shopping
            </Link>
            <ChevronRight className="w-3 h-3 opacity-60 text-white/60" />
            <span className="text-white/60 text-[12px] font-medium">{entry.name}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-white text-[26px] sm:text-[36px] font-extrabold leading-tight tracking-tight"
            style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}
          >
            {entry.name}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.18 }}
            className="flex flex-wrap items-center gap-3 mt-3"
          >
            {entry.subtitle && (
              <span className="text-white/85 text-[13px] font-medium">{entry.subtitle}</span>
            )}
            <span className="flex items-center gap-1 text-white/85 text-[13px]">
              <ShoppingBag className="w-3.5 h-3.5 text-[#51ba4f]" />
              {typeLabel}
            </span>
            {!isOnline && entry.address && !entry.address.trim().startsWith('@') && (
              <span className="flex items-center gap-1 text-white/85 text-[13px]">
                <MapPin className="w-3.5 h-3.5 text-[#51ba4f]" />
                {entry.address}
              </span>
            )}
            {isOnline && (
              <span className="flex items-center gap-1 text-white/85 text-[13px]">
                <Globe className="w-3.5 h-3.5 text-[#51ba4f]" />
                Online boutique serving Dallas-Fort Worth
              </span>
            )}
          </motion.div>

          {isFeatured && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.24 }}
              className="flex items-center gap-2 mt-4"
            >
              <p className="text-[12px] font-semibold text-[#51ba4f] tracking-wide">
                Featured by TastebudsByAnubhi
              </p>
              {hasAnySocial && <SocialLinks item={entry} px={22} />}
            </motion.div>
          )}
        </div>
      </section>

      {/* Main content */}
      <div className="max-w-[1000px] mx-auto px-4 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <h2 className="text-[18px] font-bold text-gray-900 mb-3">About {entry.name}</h2>
          <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch]">
            {entry.name} is {isOnline ? 'an online Desi boutique serving the Dallas-Fort Worth community' : `a ${typeLabel.toLowerCase()} store located in ${cityLabel}`}.
            {entry.description ? ` ${entry.description}` : ''}
            {entry.subtitle ? ` ${entry.subtitle}.` : ''}
            {!isOnline && entry.address && !entry.address.trim().startsWith('@')
              ? ` You can visit the store at ${entry.address}.`
              : ''}
            {` Browse ${entry.name} and more Indian, Pakistani, and South Asian shopping across Dallas, Frisco, Plano, Irving, McKinney, Carrollton, and the DFW metroplex on Dallas Desi Scoop.`}
          </p>

          {!isFeatured && hasAnySocial && (
            <div className="mt-6">
              <SocialLinks item={entry} px={20} />
            </div>
          )}

          {/* Products & Services */}
          <h2 className="text-[18px] font-bold text-gray-900 mt-8 mb-3">Products &amp; Services</h2>
          {allCats.length > 0 ? (
            <ul className="space-y-2">
              {allCats.map((c) => (
                <li key={c} className="flex items-start gap-2 text-[14px] text-gray-600 leading-relaxed">
                  <span className="inline-flex items-center shrink-0 mt-0.5 px-2 py-0.5 rounded-full bg-[#fff3e8] text-[#c2410c] text-[11px] font-semibold whitespace-nowrap">
                    {CATEGORY_LABELS[c] || c}
                  </span>
                  <span>{CATEGORY_OFFERINGS[c] || 'Desi shopping and lifestyle products.'}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch]">
              {entry.description || '[Coming soon]'}
            </p>
          )}
        </div>

        {/* Contact & Info */}
        <div
          className="bg-white rounded-xl border border-gray-100 p-5 h-fit"
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
        >
          <h3 className="text-[13px] font-bold uppercase tracking-wide text-gray-400 mb-4">
            Contact &amp; Info
          </h3>
          <div className="space-y-3 text-[13px] text-gray-700">
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
              <span>
                {isOnline
                  ? entry.address && entry.address.trim().startsWith('@')
                    ? `${entry.address} (Instagram)`
                    : 'Online boutique'
                  : entry.address || '[Coming soon]'}
              </span>
            </div>
            {entry.phone && (
              <a
                href={`tel:${String(entry.phone).replace(/[^\d+]/g, '')}`}
                className="flex items-start gap-2 hover:text-[#082f6d] transition-colors"
              >
                <Phone className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <span>{entry.phone}</span>
              </a>
            )}
            {entry.shopUrl && (
              <a
                href={entry.shopUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-2 hover:text-[#082f6d] transition-colors"
              >
                {entry.shopUrl.includes('facebook.com') ? (
                  <Globe className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                ) : (
                  <Instagram className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                )}
                <span>Shop / follow {entry.name}</span>
              </a>
            )}
          </div>
          {!!resolvedMapsUrl && (
            <a
              href={resolvedMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 flex items-center justify-center gap-1.5 bg-[#082f6d] hover:bg-[#062454] text-white text-[13px] font-semibold py-2.5 rounded-lg transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Get Directions
            </a>
          )}
        </div>
      </div>

      {/* Internal links: related stores */}
      {relatedStores.length > 0 && (
        <div className="max-w-[1000px] mx-auto px-4 pb-8">
          <h2 className="text-[16px] font-bold text-gray-900 mb-3">More Desi Shopping in DFW</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {relatedStores.map(({ store }: any) => (
              <Link
                key={shoppingSlug(store)}
                to={`/shopping/${shoppingSlug(store)}`}
                className="flex items-center gap-2 bg-white rounded-lg border border-gray-100 px-4 py-3 text-[13px] font-medium text-gray-700 hover:text-[#f97316] hover:border-[#f97316] transition-colors"
              >
                <ShoppingBag className="w-3.5 h-3.5 text-[#f97316] shrink-0" />
                {store.name}
                <span className="ml-auto text-gray-400 text-[11px]">
                  {(store.location || '').toLowerCase() === 'online' ? 'Online' : store.location || 'DFW'}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Internal links: explore DallasDesiScoop */}
      <div className="max-w-[1000px] mx-auto px-4 pb-12">
        <h2 className="text-[16px] font-bold text-gray-900 mb-3">Explore Dallas Desi Scoop</h2>
        <div className="flex flex-wrap gap-2">
          {[
            { to: '/shopping', label: 'All Desi Shopping in DFW' },
            { to: '/restaurants', label: 'Desi Restaurants' },
            { to: '/grocery', label: 'Indian & Pakistani Grocery Stores' },
            { to: '/events', label: 'Desi Events in DFW' },
            { to: '/blog', label: 'DFW Desi Guides' },
          ].map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="inline-flex items-center gap-1.5 bg-white rounded-full border border-gray-200 px-4 py-2 text-[12px] font-semibold text-gray-600 hover:text-[#f97316] hover:border-[#f97316] transition-colors"
            >
              {l.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}